import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';

/**
 * MembershipApiService (Angular 18) — HTTP wrappers for the /api/membership(s)
 * domain.
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/api/membershipApi/membershipApi.service.js
 *
 * Exposed as `apiService.membership.*` through the ApiService facade
 * (downgraded under the legacy name for the 10 b2b-ui consumers).
 *
 * Notes
 *  - Most endpoints use v2. Two exceptions preserved from the legacy:
 *      • `update()` and `remove()` top-level go to `/v1/api/memberships`
 *        (legacy never mutated v1 → v2 for these)
 *      • `summaries.exactSearch` uses a hardcoded URL string
 *  - QueryStringBuilderFactory replaced with `HttpParams` (idiomatic Angular).
 *    Field validation (`requires`/`optional`) is left to TypeScript types.
 */

// ============= Base URLs =============
// Hardcoded for grep-ability: pasting a URL straight from the browser's
// Network tab into a code search will land you here.

const MEMBERSHIPS_BASE_V1 = '/v1/api/memberships';
const MEMBERSHIPS_BASE_V2 = '/v2/api/memberships';
const SUMMARIES_BASE = '/v2/api/membership/summaries';
const TYPES_BASE = '/v2/api/membership/types';
const BENEFITS_BASE = '/v2/api/membership/benefits';
const LEVELS_BASE = '/v2/api/membership/levels';
const LEVELS_COLLECTION_BASE = '/v2/api/membership/levels/collection';
const ELIGIBILITY_BASE = '/v2/api/membership/eligibleoptions';
const CARDS_BASE = '/v2/api/membership/cards';
const CARDS_LAYOUTS_BASE = '/v2/api/membership/cards/layouts';
const LIFECYCLE_DISCOUNTS_BASE = '/v2/api/membership/lifecycleaction/discounts';
const LIFECYCLE_FORMS_BASE = '/v2/api/membership/lifecycleaction/forms';
const VERSIONS_BASE = '/v2/api/membership/versions';

// ============= Helpers =============

/**
 * Build `HttpParams` from a plain object, dropping `null`, `undefined` and
 * empty-string values. Replaces the legacy `QueryStringBuilderFactory`.
 */
function buildParams(props: Record<string, unknown> | null | undefined): HttpParams {
  let params = new HttpParams();
  if (props == null) return params;
  for (const [key, value] of Object.entries(props)) {
    if (value == null || value === '') continue;
    params = params.set(key, String(value));
  }
  return params;
}

interface LevelPayload {
  id?: string | number;
  name: unknown;
  subtitle: unknown;
  description: unknown;
  shortDescription: unknown;
  confirmationMessage: unknown;
  benefits: unknown;
  membershipLevelGroup: unknown;
  defaultType: unknown;
  types: unknown;
  price: unknown;
  salesChannels: unknown;
  durationInMonths: unknown;
  images: unknown;
  state: unknown;
  offerings: unknown;
  displayOrder?: unknown;
  rules: {
    numberOfCardHolders: unknown;
    canGift: unknown;
    startBeginningOfMonth: unknown;
    expireEndOfMonth: unknown;
  };
  auxiliary: unknown;
  membershipLevelCollection: unknown;
}

/** Shared body-builder for `categories.create / update / publish`. */
function buildLevelPayload(props: any, options: { includeId: boolean; includeDisplayOrder: boolean }): LevelPayload {
  const out: LevelPayload = {
    name: props.name,
    subtitle: props.subtitle,
    description: props.description,
    shortDescription: props.shortDescription,
    confirmationMessage: props.confirmationMessage,
    benefits: props.benefits,
    membershipLevelGroup: props.membershipLevelGroup,
    defaultType: props.defaultType,
    types: props.types,
    price: props.price,
    salesChannels: props.salesChannels,
    durationInMonths: props.durationInMonths,
    images: props.images,
    state: props.state,
    offerings: props.offerings,
    rules: {
      numberOfCardHolders: props.rules.numberOfCardHolders,
      canGift: props.rules.canGift,
      startBeginningOfMonth: props.rules.startBeginningOfMonth,
      expireEndOfMonth: props.rules.expireEndOfMonth,
    },
    auxiliary: props.auxiliary,
    membershipLevelCollection: props.membershipLevelCollection,
  };
  if (options.includeId) out.id = props.id;
  if (options.includeDisplayOrder) out.displayOrder = props.displayOrder;
  return out;
}

// ============= Service =============

@Injectable({ providedIn: 'root' })
export class MembershipApiService {
  private http = inject(HttpClient);

  // ---------- top-level ----------

  list(): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.get<any>(MEMBERSHIPS_BASE_V2));
  }

  detail(id: number | string): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.get<any>(`${MEMBERSHIPS_BASE_V2}/${id}`));
  }

  create(props: Record<string, unknown>): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.post<any>(MEMBERSHIPS_BASE_V2, {
        membershipMethod: props['membershipMethod'],
        level: props['level'],
        importId: props['importId'],
        membershipTypeId: props['membershipTypeId'],
        externalMembershipId: props['externalMembershipId'],
        membershipJoinedDate: props['membershipJoinedDate'],
        membershipStartDate: props['membershipStartDate'],
        membershipExpirationDate: props['membershipExpirationDate'],
        membershipStanding: props['membershipStanding'],
        membershipCategory: props['membershipCategory'],
        categoryId: props['categoryId'],
        cardHolders: props['cardHolders'],
        entitlements: props['entitlements'],
        offeringId: props['offeringId'],
        pricePointId: props['pricePointId'],
        organizationId: props['organizationId'],
        orgImportId: props['orgImportId'],
      }),
    );
  }

  // Legacy intentionally hits /v1/api/memberships (no version mutation).
  update(props: unknown): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.put<any>(MEMBERSHIPS_BASE_V1, props));
  }

  // Legacy stub — sends empty body. Kept for parity; no known consumer.
  batch(_props?: unknown): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.put<any>(MEMBERSHIPS_BASE_V1, {}));
  }

  // Legacy intentionally hits /v1/api/memberships/:id (no version mutation).
  remove(id: number | string): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.delete<any>(`${MEMBERSHIPS_BASE_V1}/${id}`));
  }

  recent(id: number | string): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.get<any>(`${MEMBERSHIPS_BASE_V2}/recent/${id}`));
  }

  categoriesList(props?: Record<string, unknown>): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.get<any>('/v2/api/membership/categories', { params: buildParams(props) }),
    );
  }

  sendDigitalCardEmail(membershipId: number): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.post<any>(`/v2/api/membership/${membershipId}/email`, null));
  }

  notes(props: { id: string; notes: string }): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.post<any>(`${MEMBERSHIPS_BASE_V2}/${props.id}/notes`, { notes: props.notes }),
    );
  }

  deleteCardholder(props: { id: number; membershipId: number }): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.delete<any>(`${MEMBERSHIPS_BASE_V2}/${props.membershipId}/cards/${props.id}`),
    );
  }

  // `checkExpirationConflicts` is declared in the legacy interface but never
  // implemented in the legacy `.service.js`. Preserve the typing surface but
  // route to a sensible v2 endpoint so any caller fails loudly rather than
  // silently with `undefined`.
  checkExpirationConflicts(props: unknown): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.post<any>(`${MEMBERSHIPS_BASE_V2}/checkexpirationconflicts`, props),
    );
  }

  // ---------- versions ----------

  readonly versions = {
    setLatest: (id: number | string): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.put<any>(`${VERSIONS_BASE}/latest/${id}`, null)),
    // Legacy uses a hardcoded URL here (not via ApiVersionFactory). Replicated.
    archive: (membershipId: number | string): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.post<any>(`/v2/api/membership/versions/archive/${membershipId}`, {})),
    unarchive: (membershipId: number | string): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.post<any>(`/v2/api/membership/versions/unarchive/${membershipId}`, {})),
  };

  // ---------- summaries ----------

  readonly summaries = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(SUMMARIES_BASE, { params: buildParams(props) })),
    search: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${SUMMARIES_BASE}/search`, { params: buildParams(props) })),
    advancedSearch: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(`${SUMMARIES_BASE}/advancedsearch`, { params: buildParams(props) }),
      ),
    // Legacy uses a hardcoded URL string here (not via ApiVersionFactory).
    exactSearch: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>('/v2/api/membership/summaries/exactsearch', {
          params: buildParams(props),
        }),
      ),
  };

  // ---------- types ----------

  readonly types = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(TYPES_BASE, { params: buildParams(props) })),
  };

  // ---------- benefits ----------

  readonly benefits = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(BENEFITS_BASE, { params: buildParams(props) })),
    create: (props: { description: string }): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.post<any>(BENEFITS_BASE, { description: props.description })),
    update: (props: {
      id: number | string;
      description: string;
    }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(BENEFITS_BASE, { id: props.id, description: props.description }),
      ),
    remove: (props: { id: number | string }): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.delete<any>(`${BENEFITS_BASE}/${props.id}`)),
  };

  // ---------- categories ----------

  readonly categories = {
    list: (props: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(LEVELS_BASE, { params: buildParams(props) })),
    detail: (id: string): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${LEVELS_BASE}/${id}`)),
    create: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(
          LEVELS_BASE,
          buildLevelPayload(props, { includeId: false, includeDisplayOrder: false }),
        ),
      ),
    update: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(
          LEVELS_BASE,
          buildLevelPayload(props, { includeId: true, includeDisplayOrder: true }),
        ),
      ),
    updateDisplayOrder: (props: unknown): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.put<any>(`${LEVELS_BASE}/displayorder`, props)),
    publish: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(
          `${LEVELS_BASE}/publish`,
          buildLevelPayload(props, { includeId: true, includeDisplayOrder: true }),
        ),
      ),
    remove: (props: { id: string }): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.delete<any>(`${LEVELS_BASE}/${props.id}`)),

    collection: {
      list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(LEVELS_COLLECTION_BASE, { params: buildParams(props) }),
        ),
      detail: (id: string): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.get<any>(`${LEVELS_COLLECTION_BASE}/${id}`)),
      create: (props: unknown): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.post<any>(LEVELS_COLLECTION_BASE, props)),
      update: (props: unknown): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.put<any>(LEVELS_COLLECTION_BASE, props)),
      remove: (id: string): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.delete<any>(`${LEVELS_COLLECTION_BASE}/${id}`)),
      updateDisplayOrder: (props: unknown): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.put<any>(`${LEVELS_COLLECTION_BASE}/displayorder`, props)),
    },
  };

  // ---------- eligibility ----------

  readonly eligibility = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(ELIGIBILITY_BASE, { params: buildParams(props) })),
    detail: (id: number | string): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${ELIGIBILITY_BASE}/${id}`)),
    create: (props: unknown): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.post<any>(ELIGIBILITY_BASE, props)),
    update: (props: unknown): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.put<any>(ELIGIBILITY_BASE, props)),
    remove: (id: number | string): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.delete<any>(`${ELIGIBILITY_BASE}/${id}`)),
    // Note: legacy URL has a trailing slash after `levels` (`/levels/?…`).
    // Preserved for backend contract parity.
    listByMembershipCategory: (props: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(`${ELIGIBILITY_BASE}/levels/`, { params: buildParams(props) }),
      ),
  };

  // ---------- offerings ----------

  readonly offerings = {
    list: (props: any): Promise<AjsResponse<any>> => {
      const url = `${LEVELS_BASE}/${props.categoryId}/membershipofferings`;
      return wrapAjs(this.http.get<any>(url, { params: buildParams(props) }));
    },
    summaries: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(`${SUMMARIES_BASE}/offering`, { params: buildParams(props) }),
      ),
    create: (offering: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(
          `${LEVELS_BASE}/${offering.categoryId}/membershipofferings`,
          {
            name: offering.name,
            subtitle: offering.subtitle,
            description: offering.description,
            duration: offering.duration,
            benefits: offering.benefits,
            rules: offering.rules,
            actions: offering.actions,
            priceLists: offering.priceLists,
            startTime: offering.startDate,
            endTime: offering.endDate,
            autorenewalType: offering.autorenewalType,
          },
        ),
      ),
    update: (offering: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(
          `${LEVELS_BASE}/${offering.categoryId}/membershipofferings`,
          {
            id: offering.id,
            name: offering.name,
            subtitle: offering.subtitle,
            description: offering.description,
            duration: offering.duration,
            benefits: offering.benefits,
            rules: offering.rules,
            actions: offering.actions,
            priceLists: offering.priceLists,
            startTime: offering.startDate,
            endTime: offering.endDate,
            types: offering.types,
            defaultType: offering.defaultType,
            autorenewalType: offering.autorenewalType,
          },
        ),
      ),
    detail: (props: { categoryId: string; offeringId: string }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(
          `${LEVELS_BASE}/${props.categoryId}/membershipofferings/${props.offeringId}`,
        ),
      ),
    remove: (props: { categoryId: string; offeringId: string }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.delete<any>(
          `${LEVELS_BASE}/${props.categoryId}/membershipofferings/${props.offeringId}`,
        ),
      ),
  };

  // ---------- cards.layouts ----------

  readonly cards = {
    layouts: {
      list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.get<any>(CARDS_LAYOUTS_BASE, { params: buildParams(props) })),
      detail: (id: number | string): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.get<any>(`${CARDS_LAYOUTS_BASE}/${id}`)),
      create: (props: unknown): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.post<any>(CARDS_LAYOUTS_BASE, props)),
      update: (props: unknown, publish?: boolean | string): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.put<any>(CARDS_LAYOUTS_BASE, props, {
            params: publish ? buildParams({ publish }) : undefined,
          }),
        ),
      remove: (id: number | string): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.delete<any>(`${CARDS_LAYOUTS_BASE}/${id}`)),
    },
  };

  // ---------- lifecycle.discounts / lifecycle.forms ----------

  readonly lifecycle = {
    discounts: {
      list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(LIFECYCLE_DISCOUNTS_BASE, { params: buildParams(props) }),
        ),
      detail: (id: number | string): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.get<any>(`${LIFECYCLE_DISCOUNTS_BASE}/${id}`)),
      create: (props: unknown): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.post<any>(LIFECYCLE_DISCOUNTS_BASE, props)),
      update: (props: unknown): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.put<any>(LIFECYCLE_DISCOUNTS_BASE, props)),
      remove: (id: number | string): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.delete<any>(`${LIFECYCLE_DISCOUNTS_BASE}/${id}`)),
    },
    forms: {
      list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.get<any>(LIFECYCLE_FORMS_BASE, { params: buildParams(props) })),
      detail: (props: { id: number | string }): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.get<any>(`${LIFECYCLE_FORMS_BASE}/${props.id}`)),
      create: (props: unknown): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.post<any>(LIFECYCLE_FORMS_BASE, props)),
      update: (props: unknown): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.put<any>(LIFECYCLE_FORMS_BASE, props)),
      remove: (props: { id: number | string }): Promise<AjsResponse<any>> =>
        wrapAjs(this.http.delete<any>(`${LIFECYCLE_FORMS_BASE}/${props.id}`)),
    },
  };

  // ---------- cardholders / validate / layouts ----------

  readonly cardholders = {
    detail: (id: string): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${CARDS_BASE}/${id}`)),
  };

  readonly validate = {
    detail: (props: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(`${MEMBERSHIPS_BASE_V2}/checkout/validate`, {
          params: buildParams(props),
        }),
      ),
  };

  // Static asset, not an API endpoint — preserved as-is from the legacy.
  readonly layouts = {
    list: (): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>('/assets/data/membership_layout.json?v=2')),
  };
}
